import { useState, useEffect, useRef, useMemo } from "react";
import { api, formatError, fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2, XCircle, HelpCircle, AlertTriangle, Ghost, PlusCircle, MinusCircle, TrendingUp, RotateCcw, History, Clock, Download, FileText, StickyNote, Undo2 } from "lucide-react";

function Stat({ label, value, tone = "slate", icon: Icon }) {
  const tones = {
    slate: "text-slate-900",
    green: "text-green-600",
    amber: "text-amber-600",
    red: "text-red-600",
    blue: "text-blue-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className={`mt-2 font-heading text-3xl font-black mono-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function ImportPanel({ onDone, goProducts }) {
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [note, setNote] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/admin/imports").then((r) => setHistory(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (note.trim()) fd.append("note", note.trim());
    try {
      const { data } = await api.post("/admin/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.stats.products_loaded} products from ${data.filename}`);
      setSelectedId(null);
      setNote("");
      load();
      onDone?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const latest = history[0] || null;
  const viewing = useMemo(
    () => history.find((h) => h.id === selectedId) || latest,
    [history, selectedId, latest]
  );
  const s = viewing?.stats;
  const isLatest = viewing && latest && viewing.id === latest.id;

  useEffect(() => { setNoteDraft(viewing?.note || ""); }, [viewing?.id]);

  const exportDiff = async () => {
    if (!viewing) return;
    try {
      const r = await api.get(`/admin/imports/${viewing.id}/diff.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `import_diff_${viewing.id.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Diff export failed"); }
  };

  const downloadDigest = async () => {
    if (!viewing) return;
    try {
      const r = await api.get(`/admin/imports/${viewing.id}/digest.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `import_digest_${viewing.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  const saveNote = async () => {
    if (!viewing) return;
    try {
      await api.put(`/admin/imports/${viewing.id}/note`, { note: noteDraft });
      toast.success("Note saved");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const undoImport = async () => {
    if (!viewing) return;
    if (!window.confirm(`Undo "${viewing.filename}"? This restores all product pricing to exactly how it was just before this import. Product images, files and customers are untouched.`)) return;
    try {
      const { data } = await api.post(`/admin/imports/${viewing.id}/undo`);
      toast.success(`Import undone — ${data.restored_products} products restored`);
      setSelectedId(null);
      load();
      onDone?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-8">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Price Import</h1>
        <p className="text-slate-500 mt-1">Drop in the latest NetSuite/Excel price list — pricing updates across all levels automatically.</p>
      </header>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files[0]); }}
        className={`rounded-sm border-2 border-dashed p-12 text-center transition-colors ${
          dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"
        }`}
        data-testid="import-dropzone"
      >
        <UploadCloud className="h-10 w-10 mx-auto text-slate-400" />
        <p className="mt-4 font-medium text-slate-700">Drag & drop your .xlsx or .csv price file</p>
        <p className="text-sm text-slate-500 mt-1">or</p>
        <input
          ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden
          onChange={(e) => doUpload(e.target.files[0])}
          data-testid="import-file-input"
        />
        <Button
          onClick={() => fileRef.current?.click()} disabled={busy}
          className="mt-3 rounded-sm bg-slate-900 hover:bg-slate-800 transition-colors"
          data-testid="import-choose-button"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {busy ? "Processing…" : "Choose file"}
        </Button>
        <div className="mt-5 max-w-md mx-auto text-left">
          <label className="text-xs uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5 mb-1">
            <StickyNote className="h-3.5 w-3.5" /> Note for this import (optional)
          </label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="e.g. Spring 2026 update"
            className="w-full px-3 py-2 rounded-sm border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="import-note-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">Shown in the history and the staff alert email.</p>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-10" data-testid="import-history">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-slate-500" /> Import history
            <span className="text-sm font-normal text-slate-400">({history.length})</span>
          </h2>
          <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-72 overflow-auto">
            {history.map((h) => {
              const active = viewing && h.id === viewing.id;
              const isTop = latest && h.id === latest.id;
              const st = h.stats || {};
              const changed = (st.new_count || 0) + (st.removed_count || 0) + (st.price_change_count || 0) + (st.reactivated_count || 0);
              return (
                <button
                  key={h.id}
                  onClick={() => setSelectedId(h.id)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
                  data-testid={`import-history-row-${h.id}`}
                >
                  <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{h.filename}</span>
                      {isTop && <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-700 rounded-sm px-1.5 py-0.5">Current</span>}
                    </div>
                    {h.note ? <div className="text-xs text-slate-600 italic truncate">“{h.note}”</div> : null}
                    <div className="text-xs text-slate-500">{new Date(h.timestamp).toLocaleString()}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500 shrink-0">
                    <span className="text-green-600">{st.products_loaded} products</span>
                    {changed > 0
                      ? <span className="text-amber-600">{changed} change{changed === 1 ? "" : "s"}</span>
                      : <span className="text-slate-400">no changes</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {viewing && s && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-slate-900">{isLatest ? "Latest import summary" : "Import summary"}</h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">
                {viewing.filename} · {new Date(viewing.timestamp).toLocaleString()} · {s.format} format
              </span>
              <Button onClick={downloadDigest} variant="outline" size="sm" className="rounded-sm" data-testid="download-digest-button">
                <FileText className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              {isLatest && (
                <Button onClick={undoImport} variant="outline" size="sm" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="undo-import-button">
                  <Undo2 className="h-4 w-4 mr-2" /> Undo this import
                </Button>
              )}
            </div>
          </div>

          <div className="mb-6 flex items-end gap-2" data-testid="import-note-editor">
            <div className="flex-1 max-w-md">
              <label className="text-xs uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5 mb-1">
                <StickyNote className="h-3.5 w-3.5" /> Note
              </label>
              <input
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                maxLength={200}
                placeholder="Add a note (e.g. Spring 2026 update)"
                className="w-full px-3 py-2 rounded-sm border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="edit-note-input"
              />
            </div>
            <Button onClick={saveNote} variant="outline" size="sm" className="rounded-sm" disabled={(viewing.note || "") === noteDraft} data-testid="save-note-button">
              Save note
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat label="Rows read" value={s.rows_read} icon={FileSpreadsheet} />
            <Stat label="Products loaded" value={s.products_loaded} tone="green" icon={CheckCircle2} />
            <Stat label="Skipped" value={s.skipped_count} tone="slate" icon={XCircle} />
            <Stat label="Unclassified" value={s.unclassified_count} tone="amber" icon={HelpCircle} />
            <Stat label="Anomalies" value={s.anomalies_count} tone="red" icon={AlertTriangle} />
            <Stat label="Orphaned" value={s.orphaned_count} tone="blue" icon={Ghost} />
          </div>

          {viewing.diff && (s.new_count + s.removed_count + s.price_change_count + s.reactivated_count > 0) ? (
            <div className="mt-8" data-testid="import-diff">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-bold text-slate-900">Changes since previous import</h3>
                <Button onClick={exportDiff} variant="outline" size="sm" className="rounded-sm" data-testid="export-diff-button">
                  <Download className="h-4 w-4 mr-2" /> Export diff CSV
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="New SKUs" value={s.new_count} tone="green" icon={PlusCircle} />
                <Stat label="Removed" value={s.removed_count} tone="red" icon={MinusCircle} />
                <Stat label="Price changes" value={s.price_change_count} tone="amber" icon={TrendingUp} />
                <Stat label="Reactivated" value={s.reactivated_count} tone="blue" icon={RotateCcw} />
              </div>

              {viewing.diff.price_changes.length > 0 && (
                <details className="mt-4" open>
                  <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-900">
                    View {viewing.diff.price_changes_total} price change{viewing.diff.price_changes_total > 1 ? "s" : ""}
                  </summary>
                  <div className="mt-2 bg-white border border-slate-200 rounded-sm max-h-80 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0">
                        <tr><th className="text-left px-4 py-2">SKU</th><th className="text-left px-4 py-2">Level</th><th className="text-right px-4 py-2">Old</th><th className="text-right px-4 py-2">New</th></tr>
                      </thead>
                      <tbody>
                        {viewing.diff.price_changes.map((c) => {
                          const up = c.new > c.old;
                          return (
                            <tr key={`${c.sku}-${c.level}`} className="border-t border-slate-100">
                              <td className="px-4 py-2 font-mono">{c.sku}</td>
                              <td className="px-4 py-2 text-slate-600">{c.level}</td>
                              <td className="px-4 py-2 text-right text-slate-400 line-through mono-nums">{fmtPrice(c.old)}</td>
                              <td className={`px-4 py-2 text-right font-medium mono-nums ${up ? "text-red-600" : "text-green-600"}`}>{fmtPrice(c.new)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {viewing.diff.new_skus.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-green-700 hover:text-green-800">{viewing.diff.new_skus.length} new SKUs added</summary>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {viewing.diff.new_skus.map((sku) => <span key={sku} className="font-mono text-xs bg-green-50 border border-green-200 text-green-800 rounded-sm px-2 py-0.5">{sku}</span>)}
                  </div>
                </details>
              )}

              {viewing.diff.removed_skus.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-red-700 hover:text-red-800">{viewing.diff.removed_skus.length} SKUs removed (now orphaned)</summary>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {viewing.diff.removed_skus.map((sku) => <span key={sku} className="font-mono text-xs bg-red-50 border border-red-200 text-red-800 rounded-sm px-2 py-0.5">{sku}</span>)}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <p className="mt-6 text-sm text-slate-400" data-testid="import-no-changes">No catalog changes recorded for this import{isLatest ? "" : " compared to the one before it"}.</p>
          )}

          {s.unclassified_count > 0 && isLatest && (
            <Card className="mt-6 rounded-sm border-amber-200 bg-amber-50 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HelpCircle className="h-5 w-5 text-amber-600" />
                  <div>
                    <div className="font-medium text-slate-900">{s.unclassified_count} SKUs need a family</div>
                    <div className="text-sm text-slate-600">Assign them so they appear on customer pricing pages.</div>
                  </div>
                </div>
                <Button onClick={goProducts} className="rounded-sm bg-amber-600 hover:bg-amber-700 transition-colors" data-testid="go-map-skus">
                  Map SKUs
                </Button>
              </div>
            </Card>
          )}

          {viewing.anomalies?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" /> Price anomalies
              </h3>
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr><th className="text-left px-4 py-2">SKU</th><th className="text-left px-4 py-2">Level</th><th className="text-left px-4 py-2">Conflicting values</th></tr>
                  </thead>
                  <tbody>
                    {viewing.anomalies.map((a) => (
                      <tr key={`${a.sku}-${a.level}`} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-mono">{a.sku}</td>
                        <td className="px-4 py-2">{a.level}</td>
                        <td className="px-4 py-2 text-red-600">{a.values.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {viewing.skipped_skus?.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-900">
                View {viewing.skipped_skus.length} skipped SKUs
              </summary>
              <div className="mt-2 bg-white border border-slate-200 rounded-sm max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {viewing.skipped_skus.map((k) => (
                      <tr key={k.sku} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-mono">{k.sku}</td>
                        <td className="px-4 py-2 text-slate-500">{k.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
